import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { TooltipPlacement, TooltipTrigger } from './tooltip.model';
import { NgStyleInterface, NzTSType } from 'ng-zorro-antd/core/types';

// Provider configuration to create an instance of NzTooltipDirective
const TooltipDirectiveProvider = {
  provide: NzTooltipDirective,
  useFactory: () => new NzTooltipDirective(),
  deps: [],
};

@Directive({
  selector: '[uiTooltip]',
  providers: [TooltipDirectiveProvider],
})
export class TooltipDirective implements AfterViewInit, OnChanges {
  private tooltip = inject(NzTooltipDirective);

  /**
   * Whether the arrow should point at the center of the origin element.
   */
  tooltipArrowPointAtCenter = input<boolean>();

  /**
   * The text content to display within the tooltip.
   */
  tooltipTitle = input<NzTSType | null | undefined>();

  /**
   * Context for the tooltip title content, used in case of templated content.
   */
  tooltipTitleContext = input<object>();

  /**
   * Trigger mode for the tooltip, e.g., 'hover', 'click', or 'focus'.
   * If null, the tooltip will not be triggered.
   */
  tooltipTrigger = input<TooltipTrigger>('hover');

  /**
   * The preferred placement of the tooltip relative to its origin element.
   */
  tooltipPlacement = input<TooltipPlacement>('top');

  /**
   * Background color for the tooltip.
   */
  tooltipColor = input<string>('#002766');

  /**
   * Reference to the origin element where the tooltip will be attached.
   */
  tooltipOrigin = input<ElementRef>();

  /**
   * Visibility state of the tooltip (true = visible, false = hidden).
   */
  tooltipVisible = input<boolean>();

  /**
   * Delay (in seconds) before the tooltip is shown upon mouse enter.
   */
  tooltipMouseEnterDelay = input<number>(0.15);

  /**
   * Delay (in seconds) before the tooltip is hidden upon mouse leave.
   */
  tooltipMouseLeaveDelay = input<number>(0.1);

  /**
   * Custom class name(s) for the tooltip's overlay container.
   */
  tooltipOverlayClassName = input<string>('tooltip-rounded');

  /**
   * Custom inline styles for the tooltip's overlay container.
   */
  tooltipOverlayStyle = input<NgStyleInterface>();

  /**
   * Handles changes to input properties and propagates them to the NzTooltipDirective instance.
   * @param changes - Object containing the changes to input properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.tooltip.ngOnChanges(changes);
  }

  /**
   * Initializes the tooltip instance and synchronizes properties with NzTooltipDirective.
   */
  ngAfterViewInit(): void {
    this.tooltip.arrowPointAtCenter = this.tooltipArrowPointAtCenter();
    this.tooltip.title = this.tooltipTitle();
    this.tooltip.titleContext = this.tooltipTitleContext();
    this.tooltip.trigger = this.tooltipTrigger();
    this.tooltip.placement = this.tooltipPlacement();
    this.tooltip.nzTooltipColor = this.tooltipColor();
    this.tooltip.origin = this.tooltipOrigin();
    this.tooltip.visible = this.tooltipVisible();
    this.tooltip.mouseEnterDelay = this.tooltipMouseEnterDelay();
    this.tooltip.mouseLeaveDelay = this.tooltipMouseLeaveDelay();
    this.tooltip.overlayClassName = this.tooltipOverlayClassName();
    this.tooltip.overlayStyle = this.tooltipOverlayStyle();

    // Invokes the tooltip's own initialization method.
    this.tooltip.ngAfterViewInit();
  }
}
